<template>
  <div id="main-wrapper">
    <NavbarDark />

    <div class="page-title">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <h2 class="ipt-title">{{ $t('Welcome!') }}</h2>
          </div>
        </div>
      </div>
    </div>

    <section class="bg-light">
      <div class="container-fluid">
        <div class="row d-flex justify-content-center align-items-center">
          <div class="col-md-6 col-lg-5">
            <div class="dashboard-wraper w-100">
              <div class="form-submit">
                <h4>{{ $t('Change Your Password') }}</h4>

                <div class="submit-section">
                  <div class="row">
                    <!-- Step 1 -->
                    <template v-if="step === 1">
                      <div class="form-group col-12">
                        <label class="mb-1">{{ $t('Enter Your Email') }}</label>
                        <input v-model="form.email" type="email" class="form-control" required />
                        <small class="text-danger" v-if="errors.email">{{ errors.email[0] }}</small>
                      </div>

                      <div class="form-group col-12 mt-3">
                        <button
                          class="btn btn-primary px-5 rounded"
                          @click="goToStep2"
                          :disabled="sendingCode"
                        >
                          <span v-if="sendingCode">
                            <i class="fa fa-spinner fa-spin me-1"></i>
                            {{ $t('Sending...') }}
                          </span>
                          <span v-else>{{ $t('Next') }}</span>
                        </button>
                      </div>
                    </template>

                    <!-- Step 2 -->
                    <template v-else>
                      <div class="form-group col-lg-6 col-md-6">
                        <label class="mb-1">{{ $t('Email') }}</label>
                        <input v-model="form.email" type="email" class="form-control" disabled />
                      </div>

                      <div class="form-group col-lg-6 col-md-6">
                        <label class="mb-1">{{ $t('Verification Code') }}</label>
                        <input v-model="form.code" type="text" class="form-control" maxlength="6" />
                        <small class="text-danger" v-if="errors.code">{{ errors.code[0] }}</small>
                      </div>

                      <div class="form-group col-md-6">
                        <label class="mb-1">{{ $t('New Password') }}</label>
                        <input v-model="form.newPassword" type="password" class="form-control" />
                        <small class="text-danger" v-if="errors.password">{{ errors.password[0] }}</small>
                      </div>

                      <div class="form-group col-12 mt-3">
                        <button
                          class="btn btn-primary px-5 rounded"
                          @click="submitPasswordChange"
                          :disabled="resetting"
                        >
                          <span v-if="resetting">
                            <i class="fa fa-spinner fa-spin me-1"></i>
                            {{ $t('Saving...') }}
                          </span>
                          <span v-else>{{ $t('Save Changes') }}</span>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FooterTop :bg="'theme-bg'" />
    <FooterDark />
    <ScrollToTop />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useI18n } from "vue-i18n";
import NavbarDark from '@/components/navbar/navbar-dark.vue'
import FooterTop from '@/components/footer/footer-top.vue';
import FooterDark from '@/components/footer/footer-dark.vue';
import ScrollToTop from '@/components/scroll-to-top.vue';

const toast = useToast();
const { t,locale } = useI18n();

const step = ref(1);
const sendingCode = ref(false);
const resetting = ref(false);

const form = ref({
  email: '',
  code: '',
  newPassword: '',
});

const errors = ref({});

const goToStep2 = async () => {
  errors.value = {};

  if (!form.value.email) {
    toast.error(t('Please enter your email'));
    return;
  }

  sendingCode.value = true;
  try {
    await axios.post('/send-email-verify-code', { email: form.value.email },{params:{ lang: locale.value } });
    toast.success(t('Verification code sent to your email'));
    step.value = 2;
  } catch (error) {
    const responseData = error.response?.data || {};
    if (responseData.errors) errors.value = responseData.errors;
    toast.error(responseData.message || t('Failed to send verification code'));
  } finally {
    sendingCode.value = false;
  }
};

const submitPasswordChange = async () => {
  errors.value = {};

  if (!form.value.code || !form.value.newPassword) {
    toast.error(t('Please fill in all fields'));
    return;
  }

  resetting.value = true;
  try {
    await axios.post('/reset-password', {
      email: form.value.email,
      code: form.value.code,
      password: form.value.newPassword,
      password_confirmation: form.value.newPassword,
    },{params:{ lang: locale.value } });

    toast.success(t('Password changed successfully'));
    step.value = 1;
    form.value = { email: '', code: '', newPassword: '' };
  } catch (error) {
    const responseData = error.response?.data || {};

    if (responseData.errors) {
      errors.value = responseData.errors;
      const fieldErrors = Object.values(errors.value).flat().join('\n');
      if (fieldErrors) toast.error(fieldErrors);
    }

    if (responseData.message) toast.error(responseData.message);

  } finally {
    resetting.value = false;
  }
};
</script>
